import { DateTime } from "luxon";
import { useCallback, useMemo, useRef } from "react";
import {
  CalendarEventStyled,
  IconContainer,
  rejectedColors,
  TimelineEventLabel,
  TimelineEventName,
  TimelineEventStatus,
  TimelineEventStatusLabel,
  timelineEventThemes,
  TimelineIconAndLabel,
  TimelineEventPopover,
  PopoverActions,
  PopoverInfo,
  PopoverEdit,
} from "./timelineevent.styled.jsx";
import { useTranslation } from "react-i18next";
import { DeclineButton } from "./declinebutton.jsx";
import { useGetAbscenceTypesOptions } from "../../mgmtview/personalprofile/newabscencemodal/newabscencemodal.constants.js";
import { calendarPatch } from "../../api/index.js";
import { queryClient } from "../../main.jsx";
import { ApproveButton } from "./approvebutton.jsx";
import { Date } from "./date.jsx";
import { Overlay } from "react-bootstrap";

const getAbscenceNumber = (causeCode) => causeCode?.replace("P", "").replace("U", "");

export const TimelineEvent = ({
  event,
  isActive,
  onEdit,
  onClose,
  disableButtons,
  isTeam,
  calendarViewStartDate,
  calendarViewEndDate,
}) => {
  const { t } = useTranslation();
  const { options, abscencesWithCategories } = useGetAbscenceTypesOptions(); // extract
  const category = abscencesWithCategories.find(
    (abscence) => getAbscenceNumber(abscence.value) === getAbscenceNumber(event.causeCode),
  );
  const target = useRef(null);

  const statusMap = {
    OPEN: t("Open", "Avoinna"),
    APPROVED: t("Approved", "Hyväksytty"),
    REJECTED: t("Rejected", "Hylätty"),
    DONE: t("Done", "Valmis"),
  };

  let causeCode;
  if (options.length && event.causeCode) {
    if (event.causeCode.endsWith("P")) {
      // paid
      causeCode = options[0].options.find(({ value }) => value === event.causeCode);
    } else if (event.causeCode.endsWith("U")) {
      // unpaid
      causeCode = options[1].options.find(({ value }) => value === event.causeCode);
    } else {
      // others
      causeCode = options[2].options.find(({ value }) => value === event.causeCode);
    }
  }

  const timelineTheme = category ? timelineEventThemes[category.categoryName] : timelineEventThemes["default"];
  const Icon = timelineTheme ? timelineTheme.Icon : null;
  const statusClassName = event.status?.toLowerCase();
  const isRejected = event.status === "REJECTED";
  const isApproved = event.status === "APPROVED";
  const isPending = event.status === "OPEN";

  const startDate = useMemo(() => DateTime.fromISO(event.start), [event.start]);
  const endDate = useMemo(() => DateTime.fromISO(event.end), [event.end]);

  const statusLabel = statusMap[event.status];
  const typeLabel = causeCode?.label ?? "";
  const dateLabel = `${startDate.toFormat("d.M")}-${endDate.toFormat("d.M")}`;

  const getEventDisplayLength = useCallback(() => {
    const displayStartDate = DateTime.fromFormat(
      DateTime.fromISO(calendarViewStartDate).toFormat("yyyy-MM-dd"),
      "yyyy-MM-dd",
    );
    const displayEndDate = DateTime.fromFormat(
      DateTime.fromISO(calendarViewEndDate).toFormat("yyyy-MM-dd"),
      "yyyy-MM-dd",
    );
    const eventRealLength = endDate.diff(startDate, ["days"]).days + 1;
    const isOverlappingBack = startDate.startOf("day") <= displayStartDate.startOf("day");
    const isOverlappingForward = !isOverlappingBack && displayEndDate.startOf("day") <= endDate.startOf("day");

    if (!isOverlappingBack && !isOverlappingForward) {
      return eventRealLength;
    }

    /* Overlaps from previous month */
    if (isOverlappingBack && !isOverlappingForward) {
      return endDate.diff(displayStartDate, ["days"]).days + 1;
    }

    /* Overlaps to the next month */
    if (isOverlappingForward && !isOverlappingBack) {
      return displayEndDate.diff(startDate, ["days"]).days;
    }
  }, [startDate, endDate, calendarViewStartDate, calendarViewEndDate]);

  //const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const approveEvent = async (e) => {
    e.stopPropagation();
    await calendarPatch(event.id, {
      ...event,
      status: "APPROVED",
    });
    queryClient.invalidateQueries({ queryKey: ["calendarEvents"] }); // refetch data
  };

  const declineEvent = async (e) => {
    e.stopPropagation();
    await calendarPatch(event.id, {
      ...event,
      status: "REJECTED",
    });
    queryClient.invalidateQueries({ queryKey: ["calendarEvents"] }); // refetch data
  };

  const eventColor = !isRejected ? timelineTheme?.textColor : rejectedColors.textColor;
  const eventBackgroundColor = !isRejected ? timelineTheme?.backgroundColor : rejectedColors.backgroundColor;
  const showLeftDate = (isTeam && getEventDisplayLength() <= 4) || (!isTeam && getEventDisplayLength() <= 1);
  const showRightDate = (isTeam && getEventDisplayLength() > 4) || (!isTeam && getEventDisplayLength() > 1);
  const showLabel = (isTeam && (getEventDisplayLength() > 3 || event.causeCode === null || event.causeCode === undefined)) || !isTeam;

  const classNames =
    isTeam && getEventDisplayLength() <= 3 ? "icon-only justify-content-center" : "justify-content-between";

  return (
    <CalendarEventStyled
      ref={target}
      colour={eventBackgroundColor}
      className={`${statusClassName} d-flex  ${classNames}`}
    //onMouseOver={() => !disableButtons && setIsOverlayVisible(true)}
    //onMouseLeave={() => !disableButtons && setIsOverlayVisible(false)}
    >
      <Overlay target={target.current} show={isActive} placement="top-start">
        {({
          placement: _placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          hasDoneInitialMeasure: _hasDoneInitialMeasure,
          ...props
        }) => (
          <div {...props}>
            {isActive && (
              <TimelineEventPopover className="timeline-event-popover" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: "16px",
                      cursor: "pointer",
                      padding: 0,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose?.();
                    }}
                    aria-label={t("Close", "Sulje")}
                  >
                    ✕
                  </button>
                </div>

                <PopoverActions>
                  <DeclineButton declineEvent={declineEvent} />
                  <ApproveButton approveEvent={approveEvent} />
                </PopoverActions>

                <PopoverInfo className="mt-2 mb-2 timeline-event-popover-info">
                  {statusLabel}: {typeLabel} {dateLabel}
                </PopoverInfo>

                <PopoverEdit onClick={onEdit}>{t("EditEvent", "Muokkaa tapahtumaa")}</PopoverEdit>
              </TimelineEventPopover>
            )}
          </div>
        )}
      </Overlay>
      <TimelineIconAndLabel className="d-flex align-items-center gap-2">
        <IconContainer>
          <Icon color={isRejected ? rejectedColors.textColor : undefined} />
        </IconContainer>
        {showLabel && (
          <TimelineEventLabel>
            <TimelineEventName colour={eventColor}>
              {/* For team calendar: if causeCode is null (masked), show "Absence" */}
              {isTeam && !causeCode
                ? (event.title || t("Absence", "Poissaolo"))
                : (causeCode ? causeCode.label : "")
              }
            </TimelineEventName>
            <TimelineEventStatus colour={eventColor}>
              <TimelineEventStatusLabel>{statusMap[event.status]}</TimelineEventStatusLabel>
              {showLeftDate && (
                <Date
                  endDate={endDate}
                  startDate={startDate}
                  isRejected={isRejected}
                  eventLengthDays={getEventDisplayLength()}
                  timelineTheme={timelineTheme}
                />
              )}
            </TimelineEventStatus>
          </TimelineEventLabel>
        )}
      </TimelineIconAndLabel>
      {showRightDate && (
        <Date
          endDate={endDate}
          startDate={startDate}
          isRejected={isRejected}
          eventLengthDays={getEventDisplayLength()}
          timelineTheme={timelineTheme}
        />
      )}
    </CalendarEventStyled>
  );
};
